/**
 * `DataTable` — table de données unique du monorepo (plan v3, phase 4).
 *
 * Remplace les implémentations maison de colonnes / tri / visibilité /
 * pagination. Tout est client : il ne reste aucune liste paginée serveur dans
 * le portail client, donc pas de mode « manuel ».
 *
 * API volontairement MINIMALE, dérivée de son premier consommateur (l'onglet
 * Parcours de la campagne). Elle ne se généralise qu'avec le deuxième.
 *
 * TanStack Table v9 : les fonctionnalités s'enregistrent explicitement dans
 * `tableFeatures`. Une state ou une méthode absente = une feature non
 * enregistrée, jamais un problème de typage.
 */
import * as React from "react";
import {
  useTable,
  type ColumnFiltersState,
  type PaginationState,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Eye,
} from "lucide-react";
import { Link } from "react-router";
import { cn } from "@repo/utils";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { Checkbox } from "./checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import {
  FEATURES,
  type ColonneDataTable,
  type ColonneMeta,
  type TableDataTable,
} from "./data-table-features";
import { FilterBar, type ConfigBarreFiltres } from "./filter-bar";

/** Référence stable : un tableau vide recréé à chaque rendu invalide les modèles. */
const DONNEES_VIDES: never[] = [];

const TAILLE_PAGE = 10;

/**
 * Une table « sans pagination » reste paginée par TanStack, sur une page assez
 * grande pour tout contenir : désactiver la feature couperait aussi le modèle
 * de lignes, et l'appelant n'a rien à savoir de ce détail.
 */
const TAILLE_TOUT = Number.MAX_SAFE_INTEGER;

/**
 * Une ligne de pied de table (moyenne, écart-type…).
 *
 * `cellule` reçoit les lignes APRÈS filtrage : une moyenne qui ignorerait les
 * filtres actifs afficherait un chiffre que la table ne montre pas.
 */
export interface LignePied<T> {
  /** Texte de la première colonne. Une fonction pour y glisser un effectif. */
  libelle: React.ReactNode | ((lignes: T[]) => React.ReactNode);
  cellule: (colonneId: string, lignes: T[]) => React.ReactNode;
}

interface EtatVide {
  titre: string;
  description?: string;
  action?: React.ReactNode;
}

export interface DataTableProps<T extends RowData> {
  colonnes: ColonneDataTable<T>[];
  donnees: T[] | undefined;
  /** Clé de persistance de la visibilité des colonnes (`localStorage`). */
  cleColonnes?: string;
  /** Rend chaque ligne navigable — vrai `<Link>`, jamais `window.location`. */
  hrefLigne?: (ligne: T) => string;
  etatChargement?: boolean;
  /** Aucune donnée du tout. */
  vide?: EtatVide;
  /** Des données, mais aucune qui passe les filtres. */
  videFiltre?: EtatVide;
  tri?: SortingState;
  onTriChange?: (tri: SortingState) => void;
  filtres?: ColumnFiltersState;
  onFiltresChange?: (filtres: ColumnFiltersState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  densite?: "confortable" | "compacte";
  /**
   * Nombre de lignes par page, ou `"toutes"` pour tout afficher.
   *
   * `"toutes"` va avec les tables de résultats : elles se lisent d'un bloc, et
   * un pied de tableau calculé sur une seule page mentirait.
   */
  taillePage?: number | "toutes";
  /**
   * `"fixe"` (défaut) : largeurs en % réparties sur toute la largeur — les
   * tables de gestion, où chaque colonne compte. `"auto"` : la table déborde et
   * défile horizontalement, chaque colonne gardant la largeur minimale de son
   * `meta.largeur` — les matrices de scores, où les colonnes se comptent par
   * dizaines et où les compresser les rendrait illisibles.
   */
  largeurColonnes?: "fixe" | "auto";
  /** Lignes de pied de table, sous les données. */
  pied?: LignePied<T>[];
  /**
   * Barre de filtres (phase 6). Déclarative, rendue par la table elle-même :
   * les facettes et leurs compteurs vivent dans l'instance TanStack, les sortir
   * obligerait chaque appelant à créer sa table à la main.
   */
  barreFiltres?: ConfigBarreFiltres;
  /**
   * Contenu libre pose dans la bande de filtres, a gauche du bouton
   * « Colonnes » : un decompte, un rappel de perimetre. Pas un emplacement a
   * boutons d'action — ceux-la vivent dans l'en-tete de page.
   */
  resume?: React.ReactNode;
  /**
   * Hauteur du bandeau fixe de l'application, sous lequel la bande de filtres
   * et la ligne de titres viennent se coller. Defaut : la navbar partagee
   * (`h-14`). `false` desactive le collage.
   */
  decalageCollant?: string | false;
}

function lireVisibilite(cle: string | undefined): Record<string, boolean> {
  if (!cle || typeof window === "undefined") return {};
  try {
    const brut = window.localStorage.getItem(`datatable:${cle}`);
    return brut ? (JSON.parse(brut) as Record<string, boolean>) : {};
  } catch {
    // Mode privé, quota, JSON corrompu : la table s'affiche complète.
    return {};
  }
}

export function DataTable<T extends RowData>({
  colonnes,
  donnees,
  cleColonnes,
  hrefLigne,
  etatChargement,
  vide,
  videFiltre,
  tri,
  onTriChange,
  filtres,
  onFiltresChange,
  pagination,
  onPaginationChange,
  densite = "confortable",
  taillePage = TAILLE_PAGE,
  largeurColonnes = "fixe",
  pied,
  barreFiltres,
  resume,
  decalageCollant = "3.5rem",
}: DataTableProps<T>) {
  const lignesParPage = taillePage === "toutes" ? TAILLE_TOUT : taillePage;
  const largeurAuto = largeurColonnes === "auto";
  // Les défauts d'abord, le choix mémorisé ensuite : une colonne masquée par
  // défaut mais affichée une fois par l'utilisateur le reste.
  const [visibilite, setVisibilite] = React.useState<Record<string, boolean>>(
    () => {
      const defauts: Record<string, boolean> = {};
      for (const colonne of colonnes) {
        if (colonne.id && colonne.meta?.masqueeParDefaut) {
          defauts[colonne.id] = false;
        }
      }
      return { ...defauts, ...lireVisibilite(cleColonnes) };
    },
  );

  React.useEffect(() => {
    if (!cleColonnes) return;
    try {
      window.localStorage.setItem(
        `datatable:${cleColonnes}`,
        JSON.stringify(visibilite),
      );
    } catch {
      // Persistance best-effort : l'affichage n'en dépend pas.
    }
  }, [cleColonnes, visibilite]);

  // Tri, filtres et pagination sont contrôlés par l'appelant quand il les
  // fournit (URL, filtres partagés), internes sinon.
  const [triInterne, setTriInterne] = React.useState<SortingState>([]);
  const [filtresInternes, setFiltresInternes] =
    React.useState<ColumnFiltersState>([]);
  const [paginationInterne, setPaginationInterne] = React.useState<
    PaginationState
  >({ pageIndex: 0, pageSize: lignesParPage });

  const table = useTable({
    features: FEATURES,
    columns: colonnes,
    data: donnees ?? DONNEES_VIDES,
    state: {
      sorting: tri ?? triInterne,
      columnFilters: filtres ?? filtresInternes,
      pagination: pagination ?? paginationInterne,
      columnVisibility: visibilite,
    },
    onSortingChange: (maj) => {
      const suivant = typeof maj === "function" ? maj(tri ?? triInterne) : maj;
      (onTriChange ?? setTriInterne)(suivant);
    },
    onColumnFiltersChange: (maj) => {
      const courant = filtres ?? filtresInternes;
      const suivant = typeof maj === "function" ? maj(courant) : maj;
      (onFiltresChange ?? setFiltresInternes)(suivant);
    },
    onPaginationChange: (maj) => {
      const courant = pagination ?? paginationInterne;
      const suivant = typeof maj === "function" ? maj(courant) : maj;
      (onPaginationChange ?? setPaginationInterne)(suivant);
    },
    onColumnVisibilityChange: (maj) => {
      const suivant = typeof maj === "function" ? maj(visibilite) : maj;
      setVisibilite(suivant);
    },
  });

  // Filtrer depuis la page 3 ne doit jamais laisser une table vide : la page
  // revient à la première dès que les filtres changent, d'où qu'ils viennent
  // (barre de filtres, URL collée, bouton Réinitialiser).
  const signatureFiltres = JSON.stringify(filtres ?? filtresInternes);
  const signaturePrecedente = React.useRef(signatureFiltres);
  React.useEffect(() => {
    if (signaturePrecedente.current === signatureFiltres) return;
    signaturePrecedente.current = signatureFiltres;
    const courante = pagination ?? paginationInterne;
    if (courante.pageIndex === 0) return;
    (onPaginationChange ?? setPaginationInterne)({
      ...courante,
      pageIndex: 0,
    });
  }, [signatureFiltres, pagination, paginationInterne, onPaginationChange]);

  const colonnesVisibles = table.getVisibleLeafColumns();
  const lignes = table.getRowModel().rows;
  const nbPages = table.getPageCount();
  const pageCourante = (pagination ?? paginationInterne).pageIndex;
  const filtreActif = (filtres ?? filtresInternes).length > 0;
  const paddingCellule = densite === "compacte" ? "py-1.5" : undefined;

  // Un seul état vide rendu à la fois, et il dit pourquoi la table est vide.
  const etatVide = filtreActif ? (videFiltre ?? vide) : vide;

  /** Colonne figée : même décalage et même fond en tête, corps et pied. */
  const classesColonne = (meta: ColonneMeta | undefined) =>
    cn(
      meta?.aDroite && "text-right",
      meta?.auCentre && "text-center",
      meta?.figee && largeurAuto && "sticky left-0 z-10 bg-surface",
    );

  /** En largeurs `auto`, `meta.largeur` devient un plancher, pas un partage. */
  const styleColonne = (meta: ColonneMeta | undefined) =>
    largeurAuto && meta?.largeur ? { minWidth: meta.largeur } : undefined;

  // La ligne de titres se colle SOUS la bande de filtres, dont la hauteur
  // change avec la largeur de l'ecran (les pills passent a la ligne). On la
  // mesure plutot que de la figer.
  const bandeRef = React.useRef<HTMLDivElement>(null);
  const [hauteurBande, setHauteurBande] = React.useState(0);
  React.useEffect(() => {
    const noeud = bandeRef.current;
    if (!noeud || typeof ResizeObserver === "undefined") return;
    const observateur = new ResizeObserver(() => {
      setHauteurBande(noeud.getBoundingClientRect().height);
    });
    observateur.observe(noeud);
    return () => observateur.disconnect();
  }, []);

  // En largeurs `auto`, la table defile horizontalement dans son propre
  // conteneur : un ancetre defilant annule tout `sticky` cale sur la fenetre,
  // donc la ligne de titres ne peut pas coller. La bande, elle, reste collante.
  const titresCollants = decalageCollant !== false && !largeurAuto;
  const hautTitres = titresCollants
    ? `calc(${decalageCollant} + ${hauteurBande}px)`
    : undefined;

  // Le pied porte sur les lignes filtrées, pas sur la page affichée : une
  // moyenne se lit sur ce que les filtres ont retenu.
  const lignesFiltrees = React.useMemo(
    () => table.getFilteredRowModel().rows.map((r) => r.original),
    [table],
  );

  const bandeFiltres = (
    // Bande de filtres logee DANS la carte, juste au-dessus de la ligne de
    // titres : filtrer et lire les colonnes sont le meme geste, ils partagent
    // donc le meme encadre. Fond blanc + filet bas, la ligne de titres en
    // dessous porte le gris.
    <div
      ref={bandeRef}
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 rounded-t-surface border-b bg-card px-4 py-3",
        decalageCollant !== false && "sticky z-30",
      )}
      style={
        decalageCollant !== false ? { top: decalageCollant } : undefined
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {barreFiltres ? (
          <FilterBar table={table as TableDataTable<T>} {...barreFiltres} />
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {resume ? (
          <div className="text-sm text-muted-foreground">{resume}</div>
        ) : null}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Eye className="h-4 w-4 mr-2" />
              Colonnes
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <p className="text-sm font-medium mb-2">Afficher</p>
            {table
              .getAllLeafColumns()
              .filter((colonne) => colonne.getCanHide())
              .map((colonne) => {
                const libelle =
                  colonne.columnDef.meta?.libelle ??
                  (typeof colonne.columnDef.header === "string"
                    ? colonne.columnDef.header
                    : colonne.id);
                return (
                  <div
                    key={colonne.id}
                    className="flex items-center gap-2 rounded-full px-2 py-1 transition-colors duration-150 hover:bg-charte-hover"
                  >
                    <Checkbox
                      id={`col-${colonne.id}`}
                      checked={colonne.getIsVisible()}
                      onCheckedChange={(coche) =>
                        colonne.toggleVisibility(!!coche)
                      }
                    />
                    <label
                      htmlFor={`col-${colonne.id}`}
                      className="text-sm cursor-pointer"
                    >
                      {libelle}
                    </label>
                  </div>
                );
              })}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return (
    <>
      {/* `overflow-clip` et non `overflow-hidden` : sans recadrage, les lignes
          peignent leur fond jusqu'aux bords carres et debordent des angles de
          la carte. `hidden` creerait un conteneur de defilement, ce qui
          annulerait le `sticky` de la ligne de titres (voir plus bas) ;
          `clip` recadre sans scrollport, donc le `sticky` tient. */}
      {/* `-outline-offset-2` sur les liens des cellules : un lien qui remplit
          sa cellule touche les angles arrondis, qui mangeaient son contour de
          focus sur la dernière ligne. Dessiné à l'intérieur, rien ne déborde. */}
      <Card className="gap-0 py-0 overflow-clip [&_td_a:focus-visible]:-outline-offset-2">
        <CardContent className="p-0">
          {bandeFiltres}
          <Table
            className={cn("w-full", !largeurAuto && "table-fixed")}
            // Sans defilement horizontal, on neutralise le conteneur : un
            // ancetre `overflow-auto` empecherait la ligne de titres de coller.
            conteneurClassName={cn(!largeurAuto && "overflow-visible")}
          >
            {/* Largeurs portées par le <colgroup> : masquer une colonne ne fait
                plus sauter les autres, et aucun colSpan n'est calculé à la main.
                Pas de <colgroup> en largeurs `auto` : c'est le contenu qui
                dimensionne, sous le plancher de `meta.largeur`. */}
            {!largeurAuto && (
              <colgroup>
                {colonnesVisibles.map((colonne) => (
                  <col
                    key={colonne.id}
                    style={{ width: colonne.columnDef.meta?.largeur }}
                  />
                ))}
              </colgroup>
            )}
            <TableHeader>
              {table.getHeaderGroups().map((groupe) => (
                <TableRow key={groupe.id}>
                  {groupe.headers.map((entete) => {
                    const triColonne = entete.column.getIsSorted();
                    const triable = entete.column.getCanSort();
                    return (
                      <TableHead
                        key={entete.id}
                        scope="col"
                        aria-sort={
                          !triColonne
                            ? undefined
                            : triColonne === "asc"
                              ? "ascending"
                              : "descending"
                        }
                        className={cn(
                          classesColonne(entete.column.columnDef.meta),
                          // Le filet bas passe en ombre interne : sur une
                          // table a bordures fusionnees, la bordure d'un `th`
                          // colle ne suit pas le defilement.
                          titresCollants &&
                            "sticky bg-surface-entete shadow-[inset_0_-1px_0_var(--charte-line)]",
                        )}
                        style={{
                          ...styleColonne(entete.column.columnDef.meta),
                          // z-index en style : la colonne figee pose deja un
                          // `z-10` par classe, et deux utilitaires `z-*` sur le
                          // meme noeud se departagent par ordre de feuille.
                          ...(titresCollants
                            ? { top: hautTitres, zIndex: 20 }
                            : null),
                        }}
                      >
                        {entete.isPlaceholder ? null : triable ? (
                          <button
                            type="button"
                            onClick={entete.column.getToggleSortingHandler()}
                            className="inline-flex items-center gap-1 hover:text-foreground"
                          >
                            <table.FlexRender header={entete} />
                            {triColonne === "asc" ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : triColonne === "desc" ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                            )}
                          </button>
                        ) : (
                          <table.FlexRender header={entete} />
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {/* Squelette DANS le <tbody> : la table n'est pas démontée, les
                  largeurs et le focus survivent au chargement. */}
              {etatChargement || donnees === undefined
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`squelette-${i}`}>
                      {colonnesVisibles.map((colonne) => (
                        <TableCell key={colonne.id} className={paddingCellule}>
                          <div className="h-4 rounded-pastille bg-muted animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : lignes.length === 0
                  ? etatVide && (
                      <TableRow>
                        <TableCell
                          colSpan={colonnesVisibles.length}
                          className="text-center py-8"
                        >
                          <p className="font-medium">{etatVide.titre}</p>
                          {etatVide.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {etatVide.description}
                            </p>
                          )}
                          {etatVide.action && (
                            <div className="mt-4">{etatVide.action}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  : lignes.map((ligne) => {
                      const href = hrefLigne?.(ligne.original as T);
                      return (
                        <TableRow
                          key={ligne.id}
                          className={cn("group", href && "cursor-pointer hover:bg-charte-hover")}
                        >
                          {ligne.getVisibleCells().map((cellule) => (
                            <TableCell
                              key={cellule.id}
                              className={cn(
                                paddingCellule,
                                classesColonne(cellule.column.columnDef.meta),
                                // La colonne figée est opaque : sans ça, elle
                                // seule resterait blanche sur la ligne survolée.
                                cellule.column.columnDef.meta?.figee &&
                                  largeurAuto &&
                                  "group-hover:bg-charte-hover-opaque",
                                href && "p-0",
                              )}
                              style={styleColonne(cellule.column.columnDef.meta)}
                            >
                              {href ? (
                                // Vrai <Link> : pas de rechargement de page, donc
                                // websocket Convex et cache de souscriptions
                                // préservés. Navigable au clavier par nature.
                                <Link
                                  to={href}
                                  className={cn(
                                    "block px-4",
                                    densite === "compacte" ? "py-1.5" : "py-2",
                                  )}
                                >
                                  <table.FlexRender cell={cellule} />
                                </Link>
                              ) : (
                                <table.FlexRender cell={cellule} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
            </TableBody>

            {/* Pied : une ligne par agrégat, la première colonne visible porte
                son libellé. Masqué tant qu'il n'y a rien à agréger — un
                « Moyenne : — » sur une table vide n'apprend rien. */}
            {pied && pied.length > 0 && lignes.length > 0 && (
              // Fond OPAQUE : le `bg-muted/50` du composant laisserait les
              // scores défiler en transparence sous la colonne figée.
              <TableFooter className="bg-surface-entete">
                {pied.map((lignePied, indexPied) => (
                  <TableRow key={indexPied}>
                    {colonnesVisibles.map((colonne, index) => (
                      <TableCell
                        key={colonne.id}
                        className={cn(
                          paddingCellule,
                          classesColonne(colonne.columnDef.meta),
                          // Même fond opaque que le pied : la cellule figée
                          // masque ce qui défile dessous au lieu de le teinter.
                          colonne.columnDef.meta?.figee &&
                            largeurAuto &&
                            "bg-surface-entete",
                        )}
                        style={styleColonne(colonne.columnDef.meta)}
                      >
                        {index === 0
                          ? typeof lignePied.libelle === "function"
                            ? lignePied.libelle(lignesFiltrees)
                            : lignePied.libelle
                          : lignePied.cellule(colonne.id, lignesFiltrees)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </CardContent>
      </Card>

      {nbPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <Button
            variant="outline"
            size="icon"
            aria-label="Page précédente"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm data">
            {pageCourante + 1} / {nbPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Page suivante"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  );
}
